/**
 * Batch exec command - dispatch multiple tasks in parallel
 *
 * Uses the Cloudflare Sandbox Worker's /api/exec endpoint for each task.
 * Since sandbox exec() blocks until done, no polling is needed.
 */

import type { BatchTaskSpec, BatchDispatchResult, BatchTaskResult, RemoteTask } from '@spacecode/shared/remote';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { generateTaskId } from '../utils/task.js';
import { buildEnvVars } from '../utils/env.js';
import { buildRemoteTask, statusFromResult } from '../utils/remote-task.js';

/**
 * Generate a unique group ID for a batch
 */
function generateGroupId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `batch-${timestamp}-${random}`;
}

/**
 * Generate a branch name from a prompt and task ID.
 * Format: claude/<slug>-<suffix>
 * - slug: first 4 meaningful words, lowercased, hyphenated (max 30 chars)
 * - suffix: last 4 chars of task ID
 */
export function generateBranchName(prompt: string, taskId: string): string {
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'is', 'it', 'this', 'that', 'with', 'as', 'by', 'all']);
  const words = prompt
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 1 && !stopWords.has(w))
    .slice(0, 4);

  const slug = words.join('-').substring(0, 30);
  const suffix = taskId.slice(-4);
  return `claude/${slug}-${suffix}`;
}

/**
 * Dispatch a single task via the sandbox worker's exec endpoint.
 * Blocks until the task completes (sandbox exec is synchronous).
 */
async function dispatchSingleTask(
  spec: BatchTaskSpec,
  taskId: string,
  groupId: string,
  branch: string,
  sharedContext: {
    token: string;
    ghToken: string | undefined;
  },
): Promise<{ task: RemoteTask; result: BatchTaskResult }> {
  const { createSandboxClient } = await import('@spacecode/sandbox-client');
  const client = createSandboxClient(SANDBOX_WORKER_URL, sharedContext.token, sandboxClientOptions(sharedContext.token));

  // Build env vars
  const envVars = buildEnvVars({
    ghToken: sharedContext.ghToken,
  });

  // Execute task (blocks until done)
  const execResult = await client.exec({
    repo: spec.repo,
    prompt: spec.prompt,
    branch,
    envVars,
  });

  const task = buildRemoteTask({
    id: taskId,
    repo: spec.repo,
    prompt: spec.prompt,
    mode: 'exec',
    status: statusFromResult(execResult.success),
    exitCode: execResult.exitCode,
    branch,
    error: execResult.success ? undefined : execResult.logs,
    groupId,
  });

  const result: BatchTaskResult = {
    taskId,
    repo: spec.repo,
    branch,
    status: 'dispatched',
    machineId: 'sandbox',
  };

  return { task, result };
}

/**
 * Dispatch multiple tasks in parallel to sandbox workers.
 */
export async function execBatch(tasks: BatchTaskSpec[]): Promise<BatchDispatchResult> {
  if (tasks.length === 0) {
    throw new Error('No tasks provided');
  }

  const { getGitHubToken } = await import('../sync/config.js');
  const { addTasks } = await import('../config.js');

  const groupId = generateGroupId();

  // Assign task IDs and branches, validate no duplicates
  const taskSpecs: Array<{ spec: BatchTaskSpec; taskId: string; branch: string }> = [];
  const repoBranchPairs = new Set<string>();

  for (const spec of tasks) {
    const taskId = generateTaskId();
    const branch = spec.branch || generateBranchName(spec.prompt, taskId);
    const key = `${spec.repo}::${branch}`;

    if (repoBranchPairs.has(key)) {
      throw new Error(`Duplicate (repo, branch) pair: ${spec.repo} on ${branch}. Provide unique branch names.`);
    }
    repoBranchPairs.add(key);
    taskSpecs.push({ spec, taskId, branch });
  }

  // Shared setup
  const token = await resolveAuthToken();
  const ghToken = await getGitHubToken();

  const sharedContext = {
    token,
    ghToken,
  };

  // Dispatch all with stagger to avoid rate limits
  const dispatchPromises = taskSpecs.map(({ spec, taskId, branch }, index) =>
    new Promise<Awaited<ReturnType<typeof dispatchSingleTask>>>((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await dispatchSingleTask(spec, taskId, groupId, branch, sharedContext);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, index * 500); // 500ms stagger between each dispatch
    })
  );

  const settledResults = await Promise.allSettled(dispatchPromises);

  // Collect results
  const taskRecords: RemoteTask[] = [];
  const batchResults: BatchTaskResult[] = [];

  for (let i = 0; i < settledResults.length; i++) {
    const settled = settledResults[i];
    const { spec, taskId, branch } = taskSpecs[i];

    if (settled.status === 'fulfilled') {
      taskRecords.push(settled.value.task);
      batchResults.push(settled.value.result);
    } else {
      batchResults.push({
        taskId,
        repo: spec.repo,
        branch,
        status: 'failed',
        error: settled.reason instanceof Error ? settled.reason.message : String(settled.reason),
      });
    }
  }

  // Save all task records atomically
  if (taskRecords.length > 0) {
    await addTasks(taskRecords);
  }

  // No background polling needed -- sandbox exec blocks until done

  const dispatched = batchResults.filter(r => r.status === 'dispatched').length;
  const failed = batchResults.filter(r => r.status === 'failed').length;

  return {
    groupId,
    total: tasks.length,
    dispatched,
    failed,
    tasks: batchResults,
  };
}
